/**
 * 地域とdepartmentIdのマッピング設定
 */

export type SectionType = 'okinawa' | 'hiroshima' | 'niigata' | 'ube' | 'base';

export type SectionConfig = {
  /** 地域名 */
  name: string;
  /** この地域に属するdepartmentIdのリスト */
  departmentIds: string[];
  /** 地域の説明（オプション） */
  description?: string;
};

/**
 * 環境変数からdepartmentIdsを取得する関数
 * JSON形式とカンマ区切り形式の両方をサポート
 */
const getDepartmentIdsFromEnv = (envKey: string): string[] => {
  const envValue = process.env[envKey];
  if (!envValue) {
    console.warn(`環境変数 ${envKey} が設定されていません`);
    return [];
  }

  try {
    // JSON形式かどうかを判定（[]で囲まれているか）
    if (envValue.trim().startsWith('[') && envValue.trim().endsWith(']')) {
      // JSON配列として解析
      const parsedArray = JSON.parse(envValue);
      if (Array.isArray(parsedArray)) {
        return parsedArray
          .map(id => String(id).trim())
          .filter(id => id.length > 0);
      } else {
        console.error(`環境変数 ${envKey} のJSON形式が配列ではありません`);
        return [];
      }
    } else {
      // カンマ区切り形式として解析（後方互換性のため）
      return envValue
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);
    }
  } catch (error) {
    console.error(`環境変数 ${envKey} のパースに失敗しました:`, error);
    console.error(
      'JSON形式の場合は正しい配列形式で記述してください。例: ["id1","id2","id3"]'
    );
    return [];
  }
};

/**
 * 動的に環境変数から設定を構築
 */
export const SECTION_CONFIGS: Record<SectionType, SectionConfig> = {
  okinawa: {
    name: '沖縄',
    departmentIds: getDepartmentIdsFromEnv('REACT_APP_OKINAWA_DEPARTMENT_IDS'),
    description: '沖縄地域の救急医療システム',
  },
  hiroshima: {
    name: '広島',
    departmentIds: getDepartmentIdsFromEnv(
      'REACT_APP_HIROSHIMA_DEPARTMENT_IDS'
    ),
    description: '広島地域の救急医療システム',
  },
  niigata: {
    name: '新潟',
    departmentIds: getDepartmentIdsFromEnv('REACT_APP_NIIGATA_DEPARTMENT_IDS'),
    description: '新潟地域の救急医療システム',
  },
  ube: {
    name: '宇部',
    departmentIds: getDepartmentIdsFromEnv('REACT_APP_UBE_DEPARTMENT_IDS'),
    description: '宇部地域の救急医療システム',
  },
  base: {
    name: 'ベース',
    departmentIds: [], // baseは他の地域に該当しない場合のデフォルト
    description: 'デフォルトの救急医療システム',
  },
};

/**
 * departmentIdから対応する地域を取得する関数
 * @param departmentId - 消防本部ID
 * @returns 対応する地域タイプ、該当しない場合は'base'
 */
export const getSectionByDepartmentId = (
  departmentId: string | null | undefined
): SectionType => {
  // departmentIdがnullまたはundefinedの場合
  if (!departmentId) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        '🔍 地域判定: Department IDが未設定のためデフォルト表示します'
      );
    }
    return 'base';
  }

  // 各地域の設定をチェックして、departmentIdが含まれる地域を探す
  for (const [sectionType, config] of Object.entries(SECTION_CONFIGS)) {
    if (config.departmentIds.includes(departmentId)) {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          `🎯 地域判定: ${config.name} (${sectionType})`
        );
      }
      return sectionType as SectionType;
    }
  }

  // どの地域にも該当しない場合はbaseを返す
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      `🔍 地域判定: "${departmentId}" はどの地域設定にもマッチしませんでした`
    );
    console.warn('設定されている地域:');
    Object.entries(SECTION_CONFIGS).forEach(([sectionType, config]) => {
      if (config.departmentIds.length > 0) {
        console.warn(
          `  - ${config.name} (${sectionType}): ${config.departmentIds.length}個のID`
        );
      }
    });
  }
  return 'base';
};

/**
 * 地域タイプから設定情報を取得する関数
 * @param sectionType - 地域タイプ
 * @returns 地域の設定情報
 */
export const getSectionConfig = (sectionType: SectionType): SectionConfig => {
  return SECTION_CONFIGS[sectionType];
};
